import React from 'react';
import { useActivities } from '../hooks/useActivities';

const ActivityFeed: React.FC<{ limit?: number }> = ({ limit = 10 }) => {
  const { data: activities = [], isLoading } = useActivities(limit);

  const getActivityIcon = (type: string, action: string) => {
    if (action === 'created') return '➕';
    if (action === 'updated') return '✏️';
    if (action === 'deleted') return '🗑️';
    if (action === 'moved') return '🚚';
    if (action === 'inventoried') return '✅';
    
    if (type === 'objet') return '📦';
    if (type === 'zone') return '🏠';
    if (type === 'box') return '📦';
    return '📝';
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      created: 'créé',
      updated: 'modifié',
      deleted: 'supprimé',
      moved: 'déplacé',
      inventoried: 'inventorié',
    };
    return labels[action] || action;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      objet: 'objet',
      zone: 'zone',
      box: 'box',
      site: 'site',
      batiment: 'bâtiment',
      piece: 'pièce',
    };
    return labels[type] || type;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4">
          <div className="animate-pulse space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="divide-y divide-gray-200">
        {activities.length > 0 ? (
          activities.map((activity) => (
            <div key={activity.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start gap-3">
                {/* Icône */}
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">
                    {getActivityIcon(activity.type, activity.action)}
                  </span>
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{activity.userName || activity.userEmail}</span>
                    {' a '}
                    <span className="font-medium">{getActionLabel(activity.action)}</span>
                    {' l\''}
                    {getTypeLabel(activity.type)}
                    {' '}
                    <span className="font-semibold text-blue-600">{activity.entityName}</span>
                  </p>
                  
                  {activity.details && (
                    <p className="text-xs text-gray-500 mt-1">{activity.details}</p>
                  )}
                  
                  <p className="text-xs text-gray-400 mt-1">
                    {formatTimeAgo(activity.createdAt)}
                  </p>
                </div>

                {/* Temps */}
                <div className="flex-shrink-0 text-xs text-gray-400">
                  {new Date(activity.createdAt).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            Aucune activité récente
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;

